import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Link2, RefreshCw } from 'lucide-react';
import API_URL from '@/config/api';
import { RenameToMatch } from './RenameToMatch';

// Staff-only: warns before installers hit "price not set" in the ProBound quote tool.
export function QuoteToolPriceCheck({ brand, reloadKey, onHighlight, onRenamed }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/staff/product-pricing/quote-tool-check?brand=${brand}`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setData(d);
      onHighlight?.({
        missing: new Set(d.missing.map((m) => m.product_id).filter(Boolean)),
        xero: new Set(d.from_xero.map((m) => m.product_id)),
      });
    } catch (_) { /* non-blocking */ }
  }, [brand, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const { missing, from_xero: fromXero, ok, checked } = data;

  if (missing.length === 0 && fromXero.length === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900 flex items-center gap-2" data-testid="quote-check-ok">
        <CheckCircle2 className="w-4 h-4 shrink-0" /> All {checked} items the ProBound quote tool uses have a price — installers won&apos;t see &quot;price not set&quot;.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="quote-check">
      {missing.length > 0 && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950" data-testid="quote-check-missing">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 font-bold"><AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" /> {missing.length} quote-tool item{missing.length === 1 ? '' : 's'} {missing.length === 1 ? 'has' : 'have'} no price — installers will see &quot;price not set&quot;</div>
            <button type="button" onClick={load} className="text-xs inline-flex items-center gap-1 text-amber-800 hover:text-amber-950" data-testid="quote-check-refresh"><RefreshCw className="w-3 h-3" /> Re-check</button>
          </div>
          <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            {missing.map((m) => (
              <li key={m.label} className="flex flex-wrap items-center gap-x-1 gap-y-1" data-testid={`quote-check-missing-${m.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                <span className="font-semibold">{m.label}</span>
                <span className="text-amber-800/80">— {m.reason}</span>
                {m.product_id && <a href={`#pricing-row-${m.product_id}`} className="underline font-semibold">jump to row</a>}
                {!m.product_id && <RenameToMatch item={m} onRenamed={() => { load(); onRenamed?.(); }} />}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-amber-800/80">Type a Trade price on the highlighted rows, or use <strong>Apply printed pricelist</strong> (top right) to fill gaps from the Ox pricelist. {ok} item{ok === 1 ? '' : 's'} already fine.</p>
        </div>
      )}
      {fromXero.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-900" data-testid="quote-check-xero">
          <span className="font-bold inline-flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> {fromXero.length} item{fromXero.length === 1 ? '' : 's'} priced from Xero</span>
          {' '}— no Trade price typed yet, so quotes use the Xero sales price ({fromXero.map((m) => `${m.product_name} £${Number(m.price).toFixed(2)}`).join(', ')}). Type a Trade price to override.
        </div>
      )}
    </div>
  );
}
